import { useNavigate, useLocation } from '@tanstack/react-router'
import { Home, BookOpen, ShoppingBag, Calendar, ShoppingCart, Heart } from 'lucide-react'
import { useTelegram } from '@/shared/lib/telegram'
import { useCart } from '@/features/cart'
import { cn } from '@/shared/lib/utils'
import { motion } from 'motion/react'

interface NavItem {
  id: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  path: string
  badge?: number
}

/**
 * UI компонент: Боковая навигация для desktop/tablet
 */
export function SideNav() {
  const navigate = useNavigate()
  const location = useLocation()
  const { hapticFeedback, webApp } = useTelegram()
  const { data: cartData } = useCart()
  const cartItemsCount = cartData?.data?.itemsCount ?? 0
  const user = webApp?.initDataUnsafe?.user

  const handleItemClick = (path: string) => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: path as any })
  }

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  const mainNavItems: NavItem[] = [
    { id: 'home', icon: Home, label: 'Главная', path: '/' },
    { id: 'diary', icon: BookOpen, label: 'Дневник', path: '/tea-diary' },
    { id: 'catalog', icon: ShoppingBag, label: 'Каталог', path: '/catalog/tea' },
    { id: 'events', icon: Calendar, label: 'События', path: '/events' },
    { id: 'favorites', icon: Heart, label: 'Избранное', path: '/want-to-drink' },
    { id: 'cart', icon: ShoppingCart, label: 'Корзина', path: '/cart', badge: cartItemsCount },
  ]

  const userName = user?.first_name || 'Гость'
  const avatarUrl = user?.photo_url

  return (
    <aside className="side-nav">
      {/* Логотип и бренд */}
      <div className="p-6 border-b border-silk dark:border-dark-border">
        <div className="flex items-center gap-3">
          <img 
            src="/logo.svg" 
            alt="Дракон и Сорока" 
            className="w-10 h-10 flex-shrink-0"
          />
          <div>
            <h1 className="font-semibold text-ink dark:text-white text-lg">
              Дракон и Сорока
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Чайный клуб
            </p>
          </div>
        </div>
      </div>

      {/* Профиль пользователя */}
      <button
        onClick={() => handleItemClick('/profile')}
        className="w-full p-4 border-b border-silk dark:border-dark-border hover:bg-silk/30 dark:hover:bg-dark-elevated transition-colors"
      >
        <div className="flex items-center gap-3">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={userName}
              className="w-10 h-10 rounded-full object-cover border-2 border-silk dark:border-dark-border"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-primary-dark/20 flex items-center justify-center border-2 border-silk dark:border-dark-border">
              <span className="text-lg font-semibold text-primary dark:text-primary-light">
                {userName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div className="flex-1 text-left">
            <p className="font-medium text-ink dark:text-white text-sm">
              {userName}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {user?.username ? `@${user.username}` : 'Открыть профиль'}
            </p>
          </div>
        </div>
      </button>

      {/* Основная навигация */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {mainNavItems.map((item) => {
          const Icon = item.icon
          const active = isActive(item.path)

          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item.path)}
              className={cn(
                'relative w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all',
                'hover:bg-silk/50 dark:hover:bg-dark-elevated',
                active && 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light',
                !active && 'text-gray-600 dark:text-gray-400'
              )}
            >
              <Icon className={cn(
                'w-5 h-5 flex-shrink-0',
                active ? 'stroke-[2.5]' : 'stroke-[1.5]'
              )} />
              <span className={cn(
                'flex-1 text-left text-sm',
                active ? 'font-semibold' : 'font-medium'
              )}>
                {item.label}
              </span>
              {item.badge && item.badge > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-primary text-white text-xs font-bold rounded-full">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
              {active && (
                <motion.div
                  layoutId="side-nav-indicator"
                  className="absolute left-0 w-1 h-8 bg-primary rounded-r-full"
                  initial={false}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
            </button>
          )
        })}
      </nav>

    </aside>
  )
}

